import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const ProfessionalInfoTab = ({ formData, errors, onChange }) => {
  const [showAddQualification, setShowAddQualification] = useState(false);
  const [showAddEmployment, setShowAddEmployment] = useState(false);

  const employmentStatusOptions = [
    { value: 'employed', label: 'Beschäftigt' },
    { value: 'unemployed', label: 'Arbeitslos' },
    { value: 'self-employed', label: 'Selbstständig' },
    { value: 'student', label: 'Student/in' },
    { value: 'retired', label: 'Rentner/in' },
    { value: 'other', label: 'Sonstiges' }
  ];

  const educationLevelOptions = [
    { value: 'hauptschule', label: 'Hauptschulabschluss' },
    { value: 'realschule', label: 'Realschulabschluss' },
    { value: 'abitur', label: 'Abitur' },
    { value: 'ausbildung', label: 'Berufsausbildung' },
    { value: 'bachelor', label: 'Bachelor' },
    { value: 'master', label: 'Master' },
    { value: 'promotion', label: 'Promotion' },
    { value: 'none', label: 'Kein Abschluss' }
  ];

  const jobSectorOptions = [
    { value: 'it', label: 'IT und Telekommunikation' },
    { value: 'healthcare', label: 'Gesundheitswesen' },
    { value: 'education', label: 'Bildung und Erziehung' },
    { value: 'manufacturing', label: 'Fertigung und Produktion' },
    { value: 'retail', label: 'Einzelhandel' },
    { value: 'hospitality', label: 'Gastgewerbe' },
    { value: 'construction', label: 'Bauwesen' },
    { value: 'finance', label: 'Finanzwesen' },
    { value: 'logistics', label: 'Logistik und Transport' },
    { value: 'other', label: 'Sonstiges' }
  ];

  const handleAddQualification = () => {
    const newQualification = {
      id: Date.now(),
      title: '',
      institution: '',
      completionDate: '',
      certificateNumber: ''
    };
    onChange('qualifications', [...formData?.qualifications, newQualification]);
    setShowAddQualification(false);
  };

  const handleRemoveQualification = (id) => {
    onChange('qualifications', formData?.qualifications?.filter(q => q?.id !== id));
  };

  const handleQualificationChange = (id, field, value) => {
    onChange('qualifications', formData?.qualifications?.map(q => 
      q?.id === id ? { ...q, [field]: value } : q
    ));
  };

  const handleAddEmployment = () => {
    const newEmployment = {
      id: Date.now(),
      employer: '',
      position: '',
      startDate: '',
      endDate: '',
      isCurrent: false
    };
    onChange('employmentHistory', [...formData?.employmentHistory, newEmployment]);
    setShowAddEmployment(false);
  };

  const handleRemoveEmployment = (id) => {
    onChange('employmentHistory', formData?.employmentHistory?.filter(e => e?.id !== id));
  };

  const handleEmploymentChange = (id, field, value) => {
    onChange('employmentHistory', formData?.employmentHistory?.map(e => 
      e?.id === id ? { ...e, [field]: value } : e
    ));
  };

  return (
    <div className="space-y-6">
      {/* Current Employment Status */}
      <div className="bg-muted/30 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="Briefcase" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Aktueller Beschäftigungsstatus</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Select
            label="Beschäftigungsstatus"
            required
            options={employmentStatusOptions}
            value={formData?.employmentStatus}
            onChange={(value) => onChange('employmentStatus', value)}
            error={errors?.employmentStatus}
            placeholder="Bitte wählen"
          />

          <Input
            label="Aktueller Beruf"
            type="text"
            value={formData?.currentOccupation}
            onChange={(e) => onChange('currentOccupation', e?.target?.value)}
            placeholder="z.B. Softwareentwickler"
          />

          <Select
            label="Branche"
            searchable
            options={jobSectorOptions}
            value={formData?.jobSector}
            onChange={(value) => onChange('jobSector', value)}
            placeholder="Bitte wählen"
          />

          <Input
            label="Berufserfahrung (Jahre)"
            type="number"
            value={formData?.yearsOfExperience}
            onChange={(e) => onChange('yearsOfExperience', e?.target?.value)}
            placeholder="0"
            min="0"
            max="60"
          />

          <Input
            label="Gewünschtes Bruttogehalt (€/Monat)"
            type="number"
            value={formData?.desiredSalary}
            onChange={(e) => onChange('desiredSalary', e?.target?.value)}
            placeholder="3000"
            min="0"
          />

          <Input
            label="Verfügbar ab"
            type="date"
            value={formData?.availableFrom}
            onChange={(e) => onChange('availableFrom', e?.target?.value)}
            min={new Date()?.toISOString()?.split('T')?.[0]}
          />
        </div>

        <div className="mt-4 space-y-3">
          <Checkbox
            label="Bereit für Vollzeitbeschäftigung"
            checked={formData?.fullTimeAvailable}
            onChange={(e) => onChange('fullTimeAvailable', e?.target?.checked)}
          />

          <Checkbox
            label="Bereit für Teilzeitbeschäftigung"
            checked={formData?.partTimeAvailable}
            onChange={(e) => onChange('partTimeAvailable', e?.target?.checked)}
          />

          <Checkbox
            label="Bereit für Schichtarbeit"
            checked={formData?.shiftWorkAvailable}
            onChange={(e) => onChange('shiftWorkAvailable', e?.target?.checked)}
          />

          <Checkbox
            label="Bereit für Wochenendarbeit"
            checked={formData?.weekendWorkAvailable}
            onChange={(e) => onChange('weekendWorkAvailable', e?.target?.checked)}
          />

          <Checkbox
            label="Bereit für Umzug"
            checked={formData?.willingToRelocate}
            onChange={(e) => onChange('willingToRelocate', e?.target?.checked)}
          />
        </div>
      </div>
      {/* Education */}
      <div className="bg-muted/30 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="GraduationCap" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Bildungsabschluss</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Höchster Bildungsabschluss"
            required
            options={educationLevelOptions}
            value={formData?.educationLevel}
            onChange={(value) => onChange('educationLevel', value)}
            error={errors?.educationLevel}
            placeholder="Bitte wählen"
          />

          <Input
            label="Abschlussjahr"
            type="number"
            value={formData?.graduationYear}
            onChange={(e) => onChange('graduationYear', e?.target?.value)}
            placeholder="2020"
            min="1950"
            max={new Date()?.getFullYear()}
          />
        </div>
      </div>
      {/* Qualifications */}
      <div className="bg-muted/30 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Icon name="Award" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-foreground">Qualifikationen und Zertifikate</h3>
          </div>
          <Button
            variant="outline"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            onClick={() => setShowAddQualification(true)}
          >
            Hinzufügen
          </Button>
        </div>

        <div className="space-y-4">
          {formData?.qualifications?.map((qual, index) => (
            <div key={qual?.id} className="p-4 bg-background rounded-lg border border-border">
              <div className="flex items-start justify-between mb-3">
                <h4 className="text-sm font-medium text-foreground">Qualifikation {index + 1}</h4>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveQualification(qual?.id)}
                >
                  <Icon name="Trash2" size={18} className="text-error" />
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input
                  label="Bezeichnung"
                  type="text"
                  value={qual?.title}
                  onChange={(e) => handleQualificationChange(qual?.id, 'title', e?.target?.value)}
                  placeholder="z.B. Fachinformatiker"
                />

                <Input
                  label="Institution"
                  type="text"
                  value={qual?.institution}
                  onChange={(e) => handleQualificationChange(qual?.id, 'institution', e?.target?.value)}
                  placeholder="z.B. IHK Berlin"
                />

                <Input
                  label="Abschlussdatum"
                  type="date"
                  value={qual?.completionDate}
                  onChange={(e) => handleQualificationChange(qual?.id, 'completionDate', e?.target?.value)}
                  max={new Date()?.toISOString()?.split('T')?.[0]}
                />

                <Input
                  label="Zertifikatsnummer"
                  type="text"
                  value={qual?.certificateNumber}
                  onChange={(e) => handleQualificationChange(qual?.id, 'certificateNumber', e?.target?.value)}
                  placeholder="Optional"
                />
              </div>
            </div>
          ))}

          {showAddQualification && (
            <div className="flex gap-2">
              <Button variant="default" size="sm" onClick={handleAddQualification}>
                Bestätigen
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowAddQualification(false)}>
                Abbrechen
              </Button>
            </div>
          )}

          {formData?.qualifications?.length === 0 && !showAddQualification && (
            <p className="text-sm text-muted-foreground text-center py-4">
              Keine Qualifikationen hinzugefügt
            </p>
          )}
        </div>
      </div>
      {/* Employment History */}
      <div className="bg-muted/30 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Icon name="History" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-foreground">Beschäftigungshistorie</h3>
          </div>
          <Button
            variant="outline"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            onClick={() => setShowAddEmployment(true)}
          >
            Hinzufügen
          </Button>
        </div>

        <div className="space-y-4">
          {formData?.employmentHistory?.map((emp, index) => (
            <div key={emp?.id} className="p-4 bg-background rounded-lg border border-border">
              <div className="flex items-start justify-between mb-3">
                <h4 className="text-sm font-medium text-foreground">Beschäftigung {index + 1}</h4>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveEmployment(emp?.id)}
                >
                  <Icon name="Trash2" size={18} className="text-error" />
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input
                  label="Arbeitgeber"
                  type="text"
                  value={emp?.employer}
                  onChange={(e) => handleEmploymentChange(emp?.id, 'employer', e?.target?.value)}
                  placeholder="Firmenname"
                />

                <Input
                  label="Position"
                  type="text"
                  value={emp?.position}
                  onChange={(e) => handleEmploymentChange(emp?.id, 'position', e?.target?.value)}
                  placeholder="Berufsbezeichnung"
                />

                <Input
                  label="Von"
                  type="date"
                  value={emp?.startDate}
                  onChange={(e) => handleEmploymentChange(emp?.id, 'startDate', e?.target?.value)}
                  max={new Date()?.toISOString()?.split('T')?.[0]}
                />

                <Input
                  label="Bis"
                  type="date"
                  value={emp?.endDate}
                  onChange={(e) => handleEmploymentChange(emp?.id, 'endDate', e?.target?.value)}
                  max={new Date()?.toISOString()?.split('T')?.[0]}
                  disabled={emp?.isCurrent}
                />

                <div className="md:col-span-2">
                  <Checkbox
                    label="Aktuelle Beschäftigung"
                    checked={emp?.isCurrent}
                    onChange={(e) => handleEmploymentChange(emp?.id, 'isCurrent', e?.target?.checked)}
                  />
                </div>
              </div>
            </div>
          ))}

          {showAddEmployment && (
            <div className="flex gap-2">
              <Button variant="default" size="sm" onClick={handleAddEmployment}>
                Bestätigen
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowAddEmployment(false)}>
                Abbrechen
              </Button>
            </div>
          )}

          {formData?.employmentHistory?.length === 0 && !showAddEmployment && (
            <p className="text-sm text-muted-foreground text-center py-4">
              Keine Beschäftigungshistorie hinzugefügt
            </p>
          )}
        </div>
      </div>
      {/* Skills and Languages */}
      <div className="bg-muted/30 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="Languages" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Fähigkeiten und Sprachen</h3>
        </div>

        <div className="space-y-4">
          <Input
            label="Fachkenntnisse"
            type="text"
            value={formData?.skills}
            onChange={(e) => onChange('skills', e?.target?.value)}
            placeholder="z.B. JavaScript, Python, Projektmanagement (kommagetrennt)"
            description="Geben Sie Ihre Fähigkeiten durch Kommas getrennt ein"
          />

          <Input
            label="Sprachkenntnisse"
            type="text"
            value={formData?.languages}
            onChange={(e) => onChange('languages', e?.target?.value)}
            placeholder="z.B. Deutsch (Muttersprache), Englisch (Fließend)"
            description="Geben Sie Sprachen mit Kenntnisstand an"
          />

          <Input
            label="Führerscheinklassen"
            type="text"
            value={formData?.drivingLicense}
            onChange={(e) => onChange('drivingLicense', e?.target?.value)}
            placeholder="z.B. B, C, CE"
          />
        </div>
      </div>
    </div>
  );
};

export default ProfessionalInfoTab;